import { Box, Fade, Tabs as MuiTabs, useMediaQuery } from '@mui/material';
import { styled } from '@mui/material/styles';
import React, { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  activeTabSet,
  activeVideoSet,
  carStatusSet,
  dragToExploreVisibilitySet,
  getActiveTab,
  getCarStatus,
  getEngineVideoUrl,
  getExteriorZoomScale,
  getPushStartVideoUrl,
  getVideoTourExists,
} from '../../store/car-slice';

import { interiorStatusSet, showVideoToggled } from '../../store/car-slice';
import constants from '../../config/constants';
import useGalleryExist from '../../hooks/use-gallery-exist';
import { useInteriorExist } from '../../hooks/use-interior-exist';
import { urlParam } from '../../utils/url-param';

import CustomTab from './tab';
import CustomMenuItem from './menu-item';
import CustomMenu from './menu';
import PurchaseButton from '../purchase/purchase-button';
import {
  currentGalleryCarIdSet,
  getAllGalleryImagesLow,
} from '../../store/gallery-slice';

const Tabs = styled(MuiTabs)<any>(
  ({ theme, direction, value: tabValue, device, carStatus }) => ({
    position:
      carStatus === 'VDP'
        ? 'absolute'
        : direction === 'landscape'
        ? 'fixed'
        : 'absolute',
    top: 10,
    left: 10,
    width: 'fit-content',
    height: '20px',
    alignItems: 'center',
    backdropFilter: constants.BLUR_STYLE.filter,
    padding: '0 10px',
    background: theme.palette.primary.contrastText,
    borderRadius: '20px',
    justifyContent: 'center',
    margin: '0 auto',
    '.MuiTabs-flexContainer': {
      display: 'flex',
      justifyContent: 'space-around',
      [theme.breakpoints.down('lg')]: {
        '&>button': {
          minHeight: '28px',
          minWidth: '70px',
        },
      },
    },
    '.MuiTabs-scroller': {
      position: 'static',
      display: 'flex',
      height: '100% !important',
    },
    '.MuiTabs-indicator': {
      background: 'white',
      borderRadius: 4,
      height: 4,
      bottom: 0,
      width: '25px',
      [theme.breakpoints.down('lg')]: {
        height: 1,
        width: `${tabValue === 'interior' ? '58px' : '40px'}  !important`,
        marginLeft: `${tabValue === 'interior' ? '4px' : '15px'}  !important`,
      },
    },
    zIndex: theme.zIndex.fab,
    [theme.breakpoints.down('lg')]: {
      top: carStatus === 'VDP' ? 5 : direction === 'landscape' ? '1.7rem' : 0,
      left: 10,
      padding: 0,
      borderRadius: '10px',
      height: '20px',
      minHeight: '37px',
    },

    '@media(max-width: 500px)': {
      left: direction === 'landscape' ? '1rem' : '.35rem',
      transform: `translateY(${
        tabValue.includes('interior') && device === 'gadget'
          ? '-70'
          : tabValue.includes('interior') && device === 'camera'
          ? '-200'
          : direction === 'landscape'
          ? '-10'
          : '0'
      }px)`,
    },
  }),
);

const CustomBox = styled(Box)<any>(({ x, y, tabheight, tabwidth, theme }) => ({
  position: 'absolute',
  left: x + tabwidth + 8,
  top: y + tabheight / 2,
  zIndex: 1000,
  [theme.breakpoints.down('lg')]: {
    left: x + tabwidth + 4,
    top: y + tabheight / 5,
  },
  [theme.breakpoints.down('md')]: {
    top: y + tabheight / 2,
  },
}));

export default function NavigationBar() {
  const pushStartVideoUrl = useSelector(getPushStartVideoUrl);
  const engineVideoUrl = useSelector(getEngineVideoUrl);

  const [tabDimensions, setTabDimensions] = useState<{
    x: number;
    y: number;
    width: number;
    height: number;
  }>({ x: 0, y: 0, width: 0, height: 0 });

  const tabRef = useRef<HTMLDivElement>(null);

  const isVideosExist = pushStartVideoUrl || engineVideoUrl;

  const device = urlParam('device');
  const dispatch = useDispatch();
  const isGalleryExist = useGalleryExist();
  const buy = urlParam('buy');
  const viewPage = urlParam('viewPage');

  // Selectors
  const exteriorZoomScale = useSelector(getExteriorZoomScale);
  const activeTab = useSelector(getActiveTab);
  const carStatus = useSelector(getCarStatus);
  const videoTourExists = useSelector(getVideoTourExists);

  // States
  const [anchorElInterior, setAnchorElInterior] =
    React.useState<null | HTMLElement>(null);
  const openInterior = Boolean(anchorElInterior);

  const [anchorElVideos, setAnchorElVideos] =
    React.useState<null | HTMLElement>(null);
  const openVideos = Boolean(anchorElVideos);

  const { interiorGalleryLow, exteriorGalleryLow } = useSelector(
    getAllGalleryImagesLow,
  );

  // Media Query
  const isPortrait = useMediaQuery('(orientation: portrait)');

  const { isInteriorExist, isCenterConsoleExist, isBackSeatExist } =
    useInteriorExist();

  // CLOSE MENU FUNCTIONS
  const closeInteriorMenu = () => {
    setAnchorElInterior(null);
  };

  const closeVideosMenu = () => {
    setAnchorElVideos(null);
  };
  const handleCloseMenu = () => {
    closeInteriorMenu();
    closeVideosMenu();
  };

  const handleScroll = () => {
    document
      .getElementById('video-tour-container')
      ?.scrollIntoView({ behavior: 'smooth' });
  };

  // TOGGLE MENU FUNCTIONS
  const toggleInteriorMenu = (e: any) => {
    setAnchorElInterior(e?.currentTarget);
    if (anchorElInterior) closeInteriorMenu();

    if (isCenterConsoleExist) {
      dispatch(interiorStatusSet('center-console'));
    } else if (isBackSeatExist) {
      dispatch(interiorStatusSet('back-seat'));
    }

    dispatch(carStatusSet('CAR'));
    dispatch(dragToExploreVisibilitySet(false));

    dispatch(activeTabSet('interior'));
  };

  const toggleVideosMenu = (e: any) => {
    setAnchorElVideos(e?.currentTarget);
    if (anchorElVideos) closeVideosMenu();
  };

  // TAB FUNCTIONS
  const clickGallery = () => {
    handleCloseMenu();
    dispatch(activeTabSet('gallery'));

    dispatch(carStatusSet('CAR'));
    //if interior 360 is not exist navigate to first interior image gallery
    if (exteriorGalleryLow.length > 0) {
      dispatch(currentGalleryCarIdSet(exteriorGalleryLow[0]?.id));
    } else if (interiorGalleryLow.length > 0) {
      //if interior gallery is not exist navigate to first exterior image gallery
      dispatch(currentGalleryCarIdSet(interiorGalleryLow[0]?.id));
    }
  };

  const clickExterior = () => {
    handleCloseMenu();
    dispatch(activeTabSet('exterior'));

    dispatch(carStatusSet('CAR'));
    dispatch(dragToExploreVisibilitySet(false));
    dispatch(activeTabSet('exterior'));
  };

  // SUBMENU FUNCTIONS
  const clickCenterConsole = () => {
    closeInteriorMenu();
    dispatch(interiorStatusSet('center-console'));
    dispatch(activeTabSet('interior'));
  };

  const clickBackSeat = () => {
    closeInteriorMenu();
    dispatch(interiorStatusSet('back-seat'));
    dispatch(activeTabSet('interior'));
  };

  const clickPushStart = () => {
    closeVideosMenu();
    dispatch(showVideoToggled());
    dispatch(activeVideoSet('push-start'));
  };

  const ClickEngineVideo = () => {
    closeVideosMenu();
    dispatch(showVideoToggled());
    dispatch(activeVideoSet('engine'));
  };

  useEffect(() => {
    const updateTabDimensions = () => {
      if (tabRef.current) {
        const { clientHeight, clientWidth, offsetLeft, offsetTop } =
          tabRef.current;
        setTabDimensions({
          x: offsetLeft,
          y: offsetTop,
          width: clientWidth,
          height: clientHeight,
        });
      }
    };

    // TODO!fix setTimeout
    // Set a timeout to delay the initial dimension update
    let timeoutId = setTimeout(() => {
      updateTabDimensions();
    }, 1500);

    // Set up a ResizeObserver to watch for size changes
    const resizeObserver = new ResizeObserver(() => {
      // Only update dimensions if the timeout is not already running
      clearTimeout(timeoutId);
      timeoutId = setTimeout(() => {
        updateTabDimensions();
      }, 1500); // Delay for resize updates
    });

    if (tabRef.current) {
      resizeObserver.observe(tabRef.current);
    }

    // Clean up the observer and timeout on unmount
    return () => {
      clearTimeout(timeoutId);
      resizeObserver.disconnect();
    };
  }, [isPortrait]); // Add dependencies as needed

  const renderTab = (
    <Tabs
      carStatus={carStatus}
      centered
      device={device}
      direction={isPortrait ? 'portrait' : 'landscape'}
      ref={tabRef}
      value={activeTab?.includes('interior') ? 'interior' : activeTab}
    >
      <CustomTab
        handleClick={clickExterior}
        label="Exterior"
        value="exterior"
      />

      {isVideosExist && (
        <CustomTab
          handleClick={toggleVideosMenu}
          iconCondition={openVideos}
          iconRender
          label="Videos"
          value="videos"
        />
      )}

      {isInteriorExist && (
        <CustomTab
          handleClick={toggleInteriorMenu}
          iconCondition={openInterior}
          iconRender
          label="Interior"
          value="interior"
        />
      )}

      {isGalleryExist && (
        <CustomTab handleClick={clickGallery} label="Gallery" value="gallery" />
      )}

      {videoTourExists && (
        <CustomTab
          handleClick={handleScroll}
          label="Video Tour"
          value="videoTour"
        />
      )}
    </Tabs>
  );

  const interiorMenu = (
    <CustomMenu
      anchorElement={anchorElInterior}
      handleClose={closeInteriorMenu}
      id="interior-menu"
      open={openInterior}
    >
      {isCenterConsoleExist && (
        <CustomMenuItem
          handleClick={clickCenterConsole}
          text="Center Console"
        />
      )}

      {isBackSeatExist && (
        <CustomMenuItem handleClick={clickBackSeat} text="Back Seat" />
      )}
    </CustomMenu>
  );

  const VideosMenu = (
    <CustomMenu
      anchorElement={anchorElVideos}
      handleClose={closeVideosMenu}
      id="videos-menu"
      open={openVideos}
    >
      {pushStartVideoUrl && (
        <CustomMenuItem handleClick={clickPushStart} text="Push Start" />
      )}

      {engineVideoUrl && (
        <CustomMenuItem handleClick={ClickEngineVideo} text="Engine" />
      )}
    </CustomMenu>
  );

  // disable icons if viewPage is admin
  if (viewPage === 'admin') {
    return null;
  }

  const showTabs = activeTab !== 'gallery' && exteriorZoomScale === 1;

  return (
    <>
      <Fade in={showTabs}>{renderTab}</Fade>
      {buy === 'true' && tabDimensions.x !== 0 && (
        <CustomBox
          tabheight={tabDimensions.height}
          tabwidth={tabDimensions.width}
          x={tabDimensions.x}
          y={tabDimensions.y}
        >
          <PurchaseButton />
        </CustomBox>
      )}
      {interiorMenu}
      {VideosMenu}
    </>
  );
}
